import dedent from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { SelectWorkflowComponent } from './common/selectcomponent'

class ClipWithPolygons extends SelectWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.clipwithpolygons',
        group: 'Spatial Constructors',
        title: 'Clip with Polygons',
        description: `
      This component creates a new table containing the geographies of the input
      table clipped to the combined boundary of the geographies in another table.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Table to clip'
        },
        {
          name: 'polygons',
          type: WorkflowComponentParamType.InputTable,
          title: 'Polygons table'
        },
        {
          name: 'geosource',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column in table to clip',
          parent: 'source',

          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'geopolygons',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column in polygons table',
          parent: 'polygons',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        }
      ]
    )
  }

  getInnerSql({ params, connection }: WorkflowComponentCodeParams) {
    switch (connection.type) {
      case Provider.BigQuery:
        return dedent`SELECT
              ST_INTERSECTION(polyg.geo, s.${params.geosource})
                AS ${params.geosource}_clipped, s.*
              FROM ${connection.quoteName(params.source as string)} s,
                (
                  SELECT ST_UNION_AGG(${params.geopolygons}) geo
                  FROM ${connection.quoteName(params.polygons as string)}
                ) polyg
              WHERE ST_INTERSECTS(polyg.geo, s.${params.geosource})`
      case Provider.Snowflake:
        return dedent`SELECT
              ST_INTERSECTION(polyg.geo, s.${params.geosource})
                AS ${params.geosource}_clipped, s.*
              FROM ${connection.quoteName(params.source as string)} s,
                (
                  SELECT ST_COLLECT(${params.geopolygons}) geo
                  FROM ${connection.quoteName(params.polygons as string)}
                ) polyg
              WHERE ST_INTERSECTS(polyg.geo, s.${params.geosource})`
      case Provider.Postgres:
      case Provider.Redshift:
        return dedent`SELECT
                ST_INTERSECTION(polyg.geo, s.${params.geosource})
                  AS ${params.geosource}_clipped, s.*
                FROM ${connection.quoteName(params.source as string)} s,
                  (
                    SELECT ST_COLLECT(${params.geopolygons}) geo
                    FROM ${connection.quoteName(params.polygons as string)}
                  ) polyg
                WHERE ST_INTERSECTS(polyg.geo, s.${params.geosource})`
      case Provider.DatabricksRest:
        return dedent`SELECT /*+ REPARTITION(40, ${params.geosource}_clipped) */
              ${connection.processOutputGeogColumn(`ST_INTERSECTION(polyg.geo, s.${params.geosource}_processed)`)}
                AS ${params.geosource}_clipped, s.* EXCEPT(${params.geosource}_processed)
              FROM
                (
                  SELECT /*+ REPARTITION(40) */ *, ${connection.processInputGeogColumn(
                    params.geosource as string
                  )} AS ${params.geosource}_processed FROM
                  ${connection.quoteName(params.source as string)}
                ) s,
                (
                  SELECT /*+ REPARTITION(40) */ ST_UNION_AGG(${connection.processInputGeogColumn(
                    params.geopolygons as string
                  )}) geo
                  FROM ${connection.quoteName(params.polygons as string)}
                ) polyg
              WHERE ST_INTERSECTS(polyg.geo, s.${params.geosource}_processed)`
      case Provider.Oracle:
        return dedent`SELECT
                SDO_GEOM.SDO_INTERSECTION(polyg.geo, s.${params.geosource}, 0.005)
                  AS ${params.geosource}_clipped, s.*
                FROM ${connection.quoteName(params.source as string)} s,
                  (
                    SELECT SDO_AGGR_UNION(SDOAGGRTYPE(${params.geopolygons}, 0.005)) geo
                    FROM ${connection.quoteName(params.polygons as string)}
                  ) polyg
                WHERE SDO_RELATE(s.${params.geosource}, polyg.geo, 'mask=ANYINTERACT') = 'TRUE'`
      default:
        return null
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns }: GetOutputSchemaParams): TableSchema {
    return columns.source
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Snowflake, Provider.Postgres, Provider.Redshift, Provider.Oracle]
  }
}

export { ClipWithPolygons }
